import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { adminListAnalyses } from '../../utils/api';
import AdminNav from './AdminNav';

const MODALITIES = ['xray', 'skin', 'ultrasound', 'breast', 'retina', 'foot'];
const REVIEW_STATUSES = ['pending_review', 'verified', 'rejected'];
const PAGE_SIZE = 25;

const AdminAnalyses = () => {
  const { currentUser } = useAuth();
  const [modality, setModality] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminListAnalyses(currentUser.token, {
      modality: modality || undefined,
      reviewStatus: reviewStatus || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      page,
      pageSize: PAGE_SIZE,
    })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load analyses'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentUser, modality, reviewStatus, dateFrom, dateTo, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          <h1 className="heading-secondary mb-2">Superadmin Dashboard</h1>
          <p className="text-gray-400 mb-6">All analyses across every user and modality.</p>
          <AdminNav />

          <div className="flex flex-wrap gap-3 mb-6">
            <select
              value={modality}
              onChange={(e) => { setModality(e.target.value); setPage(1); }}
              className="bg-gray-900 border border-gray-800 rounded-md px-3 py-2 text-sm text-gray-300"
            >
              <option value="">All modalities</option>
              {MODALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              value={reviewStatus}
              onChange={(e) => { setReviewStatus(e.target.value); setPage(1); }}
              className="bg-gray-900 border border-gray-800 rounded-md px-3 py-2 text-sm text-gray-300"
            >
              <option value="">All review statuses</option>
              {REVIEW_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="bg-gray-900 border border-gray-800 rounded-md px-3 py-2 text-sm text-gray-300"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="bg-gray-900 border border-gray-800 rounded-md px-3 py-2 text-sm text-gray-300"
            />
          </div>

          {error && <p className="text-red-400 mb-4">{error}</p>}
          {loading ? (
            <p className="text-gray-400">Loading…</p>
          ) : data.items.length === 0 ? (
            <p className="text-gray-500">No analyses match these filters.</p>
          ) : (
            <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-800/60 text-gray-400">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Modality</th>
                    <th className="text-left px-4 py-2">User</th>
                    <th className="text-left px-4 py-2">Top finding</th>
                    <th className="text-left px-4 py-2">Review status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((a) => (
                    <tr key={a.id} className="border-t border-gray-800 hover:bg-gray-800/40">
                      <td className="px-4 py-2">
                        <Link to={`/admin/analyses/${a.id}`} className="block text-gray-300 hover:text-purple-400">
                          {a.date ? new Date(a.date).toLocaleString() : '—'}
                        </Link>
                      </td>
                      <td className="px-4 py-2 capitalize">{a.type}</td>
                      <td className="px-4 py-2 text-gray-400">{a.user_id}</td>
                      <td className="px-4 py-2">{a.predictions?.[0]?.label || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          a.review_status === 'verified' ? 'bg-green-900/40 text-green-400' :
                          a.review_status === 'rejected' ? 'bg-red-900/40 text-red-400' :
                          'bg-yellow-900/40 text-yellow-400'
                        }`}>
                          {a.review_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
            <span>{data.total} total</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded border border-gray-800 disabled:opacity-40"
              >
                Prev
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded border border-gray-800 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminAnalyses;
